<script>
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductPrice from "@ivideon/components/ProductPrice";
    import GiftLabel from "@ivideon/components/GiftLabel";
    import ProductCardIcons from "@ivideon/components/ProductCardIcons";

    export default {
        name: "ProductCard",
        components: {
            ProductCardIcons,
            GiftLabel,
            ProductPrice,
            ProductCardButtons,
        },
        props: {
            product: Object,
            position: Number,
            isSmall: Boolean,
            isRectangle: Boolean,
            inCartList: Array,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                previewImage,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                isPreOrder,
                previewImageFromOffer: "",
                offerForParams: !this.product.isClone ? undefined : currentOffers[0].id,
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            nameProduct() {
                return this.product.name;
            },
            sizeButton() {
                return this.isSmall ? "sm" : "md";
            },
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug, offer: this.offerForParams },
                };
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
        },
    };
</script>

<template>
    <div
        class="product-card"
        :class="{
            'product-card--sm': isSmall,
            'product-card--rect': isRectangle,
        }"
    >
        <gift-label v-if="product.showGiftIcon" :is-mobile="isSmall"></gift-label>

        <div class="product-card__header">
            <div class="product-card__img">
                <router-link
                    :to="productPath"
                    class="product-card__img-link js-lazy-load"
                    tabindex="-1"
                    @click.native.prevent="sendDataLayerEvent"
                >
                    <img class="product-card__img-product" :data-src="apiUrl(previewImage)" />
                    <div class="product-card__preloader">
                        <preloader :show="true" lg-size></preloader>
                    </div>
                </router-link>
            </div>
        </div>

        <router-link
            :to="productPath"
            class="product-card__wrap"
            @click.native.prevent="sendDataLayerEvent"
        >
            <div class="product-card__price">
                <product-price
                    :price="discountPrice"
                    :old-price="price"
                    :discount-count="discountPercent"
                    is-card-size
                ></product-price>
            </div>

            <div class="product-card__name">
                {{ nameProduct }}
            </div>
        </router-link>

        <div class="product-card__icons">
            <product-card-icons :icons="product.labels"></product-card-icons>
        </div>

        <product-card-buttons
            :size-button="sizeButton"
            :offer-id="offerId"
            :product="product"
            :is-pre-order="isPreOrder"
            :in-cart="inCart"
            :discount-price="discountPrice"
            initial-text="В корзину"
            @on-added-to-cart="inCart = true"
        ></product-card-buttons>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: #fff;
        box-shadow: var(--primary-shadow);
        border-radius: 15px;
        transition: box-shadow 0.2s ease-in;
        &.swiper-slide {
            height: auto;
        }
        &::before {
            content: "";
            @include center-absolute(-2px);
            z-index: -1;
            background-image: linear-gradient(
                135deg,
                #0095ff 0%,
                #14acae 46.05%,
                #1fdf47 100%
            );
            border-radius: 18px;
            opacity: 0;
            transition: opacity 0.2s ease-in;
        }

        @include bp($bp-desktop-sm) {
            border-radius: 20px;
            &::before {
                @include center-absolute(-3px);
                border-radius: 22px;
            }
        }

        &:hover {
            box-shadow: none;
            &::before {
                opacity: 1;
            }
            .product-card__img-link {
                img {
                    transform: scale(1.03);
                }
            }
        }
    }

    .product-card__header {
        position: relative;
        padding: 8px;

        @include bp($bp-desktop-sm) {
            padding: 12px;
        }
    }

    .product-card__img {
        border-radius: 8px;
        transition: 0.3s ease-in-out background-color;

        @include bp($bp-desktop-sm) {
            border-radius: 15px;
        }
    }

    .product-card__img-link {
        position: relative;
        display: block;
        padding-bottom: 100%;
        overflow: hidden;

        img {
            @include center-absolute(0);
            margin: auto;
            max-height: 100%;
            transform: translateZ(0);
            will-change: transform;
            transition: 0.3s ease-in-out transform, 0.3s ease-in-out filter;
        }
    }

    .product-card__preloader {
        opacity: 1;
    }

    .product-card__img-product {
        z-index: 1;
        display: none;
        animation: fadeIn 0.3s ease-in-out;

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: scale(0.97);
            }
            to {
                opacity: 1;
                transform: scale(1);
            }
        }

        &[lazy="loaded"] {
            display: block;
            & + .product-card__preloader {
                opacity: 0;
            }
        }
    }

    .product-card__wrap {
        display: block;
        margin-top: 4px;
        padding: 0 8px;

        @include bp($bp-desktop-sm) {
            padding: 0 12px;
        }
    }

    .product-card__name {
        display: block;
        position: relative;
        z-index: 2;
        margin-bottom: 6px;
        color: var(--primary-text);
        font-size: 12px;
        font-weight: 700;
        line-height: 16px;
        letter-spacing: -0.02em;
        transition: 0.3s ease-in-out all;

        @include bp($bp-desktop-sm) {
            margin-bottom: 8px;
            font-size: 16px;
            line-height: 20px;
            letter-spacing: 0;
        }
    }

    .product-card__price {
        display: block;
        margin-bottom: 4px;
    }

    .product-card__icons {
        padding: 0 8px;
        margin-bottom: 12px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 15px;
            padding: 0 12px;
        }
    }

    .product-card__controls {
        margin-top: auto;
        padding: 0 8px 10px;

        @include bp($bp-desktop-sm) {
            padding: 0 12px 14px;
        }
    }

    //
    // --mod
    //

    .product-card--rect {
        .product-card__img-link {
            //padding-bottom: 91%;
            @include bp($bp-desktop-sm) {
                padding-bottom: 91%;
            }
        }
    }

    .product-card--sm {
        box-shadow: 0 0 10px #f2f2f5;

        .product-card__header {
            padding: 4px;

            @include bp($bp-desktop-sm) {
                padding: 8px;
            }
        }

        .product-card__wrap {
            padding: 0 8px;

            @include bp($bp-desktop-sm) {
                padding: 0 12px;
            }
        }

        .product-card__controls {
            padding: 0 8px 8px;

            @include bp($bp-desktop-sm) {
                padding: 0 12px 12px;
            }
        }

        .product-price__number {
            font-size: 18px;
            line-height: 26px;
        }
    }
</style>
